import { ChangeEvent, useState } from 'react';

import { Box, Grid, PopoverProps, RadioGroup, ThemeProvider, Typography } from '@mui/material';
import { EnteredAddress, ValidAddresses } from '@models/validation';
import { formatAddress } from '@utils/address';
import composeTheme from '@theme/theme';
import { AddressSuggestion } from './AddressSuggestion';
import { Dialog } from '@components/dialog/Dialog';

const enteredAddressOption = {
  key: 'enteredAddressOption',
  label: 'Use the address exactly as entered',
  warning: 'Note: This address will be referred to the LIO team for review',
};

interface IPopover extends PopoverProps {
  enteredAddress: EnteredAddress;
  addressOptions?: ValidAddresses;
  onPopoverClose: () => void;
  onAddressConfirm: (updatedAddress?: EnteredAddress) => void;
  onSelectAddress: (a: EnteredAddress) => void;
  isProxyAddress?: boolean;
  onInvalidAdress?: () => void;
}

export const NoMatchingAddress = ({
  enteredAddress,
  addressOptions,
  onPopoverClose,
  onAddressConfirm,
  onSelectAddress,
  isProxyAddress,
  onInvalidAdress,
  ...other
}: IPopover) => {
  const [checkedAddress, setCheckedAddress] = useState<number | string | undefined>(undefined);

  const handleCheck = (event: ChangeEvent<HTMLInputElement>, value: string | number) => {
    setCheckedAddress(value);
  };

  const handleEditAddress = () => {
    onPopoverClose();
    // we might want a setTimeout here to align with the popover animation.
    setCheckedAddress(undefined);
  };

  const handleSelectedAddressConfirm = () => {
    let verifiedAddress = enteredAddress;
    if (checkedAddress === enteredAddressOption.key) {
      if (onInvalidAdress) {
        onInvalidAdress();
      }
    } else if (addressOptions?.length) {
      const addressId = Number(checkedAddress);
      const selectedAddress = addressOptions[addressId].address;

      verifiedAddress = {
        id: enteredAddress.id,
        street1: selectedAddress.street1,
        street2: selectedAddress.street2,
        state: selectedAddress.state,
        city: selectedAddress.city,
        zipCode: selectedAddress.zipcode,
      };

      onSelectAddress({
        id: enteredAddress.id,
        street1: selectedAddress.street1,
        street2: selectedAddress.street2,
        state: selectedAddress.state,
        city: selectedAddress.city,
        zipCode: selectedAddress.zipcode,
      });
    }

    onAddressConfirm(verifiedAddress);
    onPopoverClose();
  };

  const noMatchesFound = addressOptions?.length === 0;

  return (
    <ThemeProvider theme={composeTheme('light')}>
      <Dialog
        showBorders={false}
        open={other?.open}
        maxWidth='sm'
        fullWidth
        primaryBtnText={!isProxyAddress || addressOptions?.length ? 'Use Selected Address' : ''}
        isPrimaryBtnDisabled={!checkedAddress}
        secondaryBtnText={'Edit Address'}
        primaryBtnAction={handleSelectedAddressConfirm}
        secondaryBtnAction={handleEditAddress}
      >
        <Grid sx={{ p: 2 }}>
          <Typography
            variant='h5'
            mb={1}
            sx={{
              display: 'block',
              paddingBottom: '8px',
            }}
          >
            No matching address
          </Typography>
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              color: 'common.black',
              paddingBottom: '8px',
              mb: 0.5,
            }}
          >
            Please ensure you’ve entered the street number and name correctly.
          </Typography>

          <Typography
            variant={noMatchesFound ? 'caption' : 'bodySmall'}
            fontWeight={noMatchesFound ? 'normal' : 'bold'}
            sx={{
              display: 'block',
              color: 'common.black',
              mb: 0.5,
            }}
          >
            {noMatchesFound
              ? 'There are no suggestions that are close to entered location'
              : 'Do any of these addresses match the location?'}
          </Typography>
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <RadioGroup onChange={handleCheck}>
                {addressOptions?.map((item, index) => {
                  const option = formatAddress(item.address);
                  return (
                    <AddressSuggestion
                      key={option}
                      value={index}
                      option={option}
                      checked={Number(checkedAddress) === index}
                    />
                  );
                })}
                {!isProxyAddress ? (
                  <Box>
                    <Typography
                      variant={noMatchesFound ? 'caption' : 'bodySmall'}
                      fontWeight='bold'
                      sx={{
                        display: 'block',
                        color: 'common.black',
                        padding: '8px 0px 9px 0px',
                      }}
                    >
                      Use the address as entered:
                    </Typography>
                    <AddressSuggestion
                      value={enteredAddressOption.key}
                      option={formatAddress(enteredAddress)}
                      checked={checkedAddress === enteredAddressOption.key}
                      warning={enteredAddressOption.warning}
                    />
                  </Box>
                ) : null}
              </RadioGroup>
            </Grid>
          </Grid>
        </Grid>
      </Dialog>
    </ThemeProvider>
  );
};
